import type { LedgerAccount } from 'hooks/customerApi';
import clsx from 'clsx';
import { makeLedgerAccountPath } from 'components/SideNav/useDashboardNavTree';
import {
  useGetLedgerAccountsQuery,
  useListLedgerEntriesQuery,
  useListLedgerEntryGroupsQuery,
} from 'hooks/customerApi';
import { useCustomerApiContext } from 'hooks/useCustomerApiContext';
import React, { Suspense, useState } from 'react';
import { makePath } from 'utils/routes';
import { useLedger } from 'wrappers/LedgerProvider/LedgerProvider';
import { useWorkspaceId } from 'wrappers/WorkspaceProvider/WorkspaceProvider';

import { isEncodedEntryId } from '@fragment/shared-utils/ids/ledgerEntry';
import { Amount } from '@fragment/ui/src/components/Amount/Amount';
import { Icon } from '@fragment/ui/src/components/Icon';
import { AccountTypeIcon } from '@fragment/ui/src/components/Icon/AccountTypeIcon/AccountTypeIcon';
import { EntryIcon } from '@fragment/ui/src/components/Icon/EntryIcon/EntryIcon';
import { TextInput } from '@fragment/ui/src/components/Input/TextInput';
import { Link } from '@fragment/ui/src/components/Link';
import { LoadingIndicator } from '@fragment/ui/src/components/LoadingText';
import { Modal } from '@fragment/ui/src/components/Modal/Modal';
import { Tooltip } from '@fragment/ui/src/components/Tooltip/Tooltip';

const formatPath = (path: string) => {
  const keys = path.split('/');
  const formatTemplateValue = (value: string) => {
    if (value.length < 8) {
      return value;
    }
    return `${value.substring(0, 4)}…${value.substring(
      value.length - 4,
      value.length
    )}`;
  };
  const formatKey = (key: string, isLast: boolean) => {
    if (!isLast) {
      return `${key.length > 2 ? key.substring(0, 2) : key}\u2026`;
    }
    if (key.includes(':')) {
      const [templateKey, templateValue] = key.split(':');
      return `${templateKey}:${formatTemplateValue(templateValue)}`;
    }
    return key;
  };
  if (keys.length === 1) {
    return formatKey(keys[0], true);
  }
  return [
    '',
    ...keys.slice(1).map((key, idx) => formatKey(key, idx === keys.length - 2)),
  ].join('/');
};

type SearchProps = {
  searchTerm: string;
  onNavigate: VoidFunction;
  schemaKey: string | undefined;
  ledgerId: string;
};

const AccountSearch = ({
  searchTerm,
  onNavigate,
  schemaKey,
  ledgerId,
}: SearchProps) => {
  const { context, token } = useCustomerApiContext();
  const workspaceId = useWorkspaceId();
  const [{ data }] = useGetLedgerAccountsQuery({
    context,
    pause: !token,
    variables: {
      ledger: {
        id: ledgerId,
      },
      filter: {
        path: {
          contains: searchTerm,
        },
      },
    },
  });
  const accounts = data?.ledger?.ledgerAccounts.nodes.sort((acc1, acc2) =>
    acc1.path.localeCompare(acc2.path)
  );
  if (!accounts || accounts.length === 0 || !schemaKey) {
    return null;
  }

  const formatBalances = (balances: LedgerAccount['balances']) => {
    if (balances.nodes.length === 0) {
      return <span className="text-main-500">None</span>;
    }
    if (balances.nodes.length > 1) {
      return <span>Multiple</span>;
    }
    return (
      <Amount
        amount={balances.nodes[0].amount}
        currency={balances.nodes[0].currency}
      />
    );
  };
  return (
    <div className="flex flex-col pt-f2">
      <span className="text-main-500">Account</span>
      {accounts.map((acc) => (
        <Link
          key={acc.id}
          href={makeLedgerAccountPath({
            workspaceId,
            schemaKey,
            ledgerId,
            accountId: acc.id,
          })}
          onClick={onNavigate}
          className="border-[0.5px] border-b-0 last:border-b-[0.5px] border-main-300 hover:bg-negative"
        >
          <div className="flex flex-row justify-between w-full whitespace-nowrap space-x-[1ch]">
            <div className="flex flex-row items-center w-full min-w-f0">
              <AccountTypeIcon type={acc.type} />
              <Tooltip
                delayHide={0}
                delayShow={300}
                offset={[0, 0]}
                baseComponent={
                  <div className="w-full truncate">{formatPath(acc.path)}</div>
                }
                tooltipComponent={
                  <div className="border bg-negative border-canvas z-30 flex items-center">
                    {acc.path}
                  </div>
                }
                placement="top-start"
              />
            </div>
            {formatBalances(acc.balances)}
          </div>
        </Link>
      ))}
    </div>
  );
};

const EntrySearch = ({
  searchTerm,
  onNavigate,
  schemaKey,
  ledgerId,
}: SearchProps) => {
  const { context, token } = useCustomerApiContext();
  const [{ data }] = useListLedgerEntriesQuery({
    context,
    pause: !token,
    variables: {
      ledgerId,
      filter: {
        ledgerEntry: {
          equalTo: isEncodedEntryId(searchTerm)
            ? {
                id: searchTerm,
              }
            : {
                ik: searchTerm,
              },
        },
      },
    },
  });
  const entries = data?.ledger?.ledgerEntries.nodes;
  if (!entries || entries.length === 0 || !schemaKey) {
    return null;
  }
  return (
    <div className="flex flex-col pt-f2">
      <span className="text-main-500">Entry</span>
      {entries.map((entry) => (
        <Link
          key={entry.id}
          href={makePath([ledgerId, 'entries', entry.id])}
          onClick={onNavigate}
        >
          <div className="flex flex-row justify-between border-[0.5px] border-b-0 last:border-b-[0.5px] border-main-300 hover:bg-negative">
            <div className="flex flex-row items-center">
              <EntryIcon
                types={entry.lines.nodes.map((line) => line.account.type)}
              />
              {entry.type}
            </div>
            {entry.posted}
          </div>
        </Link>
      ))}
    </div>
  );
};

const GroupSearch = ({
  searchTerm,
  onNavigate,
  schemaKey,
  ledgerId,
}: SearchProps) => {
  const { context, token } = useCustomerApiContext();
  const [{ data: keyData }] = useListLedgerEntryGroupsQuery({
    context,
    pause: !token,
    variables: {
      ledgerId,
      filter: {
        key: {
          equalTo: searchTerm,
        },
      },
    },
  });
  const [{ data: valueData }] = useListLedgerEntryGroupsQuery({
    context,
    pause: !token,
    variables: {
      ledgerId,
      filter: {
        value: {
          equalTo: searchTerm,
        },
      },
    },
  });
  const isKeyValueSearch = searchTerm.split(':').length === 2;
  const [{ data: keyValueData }] = useListLedgerEntryGroupsQuery({
    context,
    pause: !token && isKeyValueSearch,
    variables: {
      ledgerId,
      filter: {
        key: {
          equalTo: searchTerm.split(':')[0],
        },
        value: {
          equalTo: searchTerm.split(':')[1],
        },
      },
    },
  });
  const groups = [
    ...(keyData?.ledger?.ledgerEntryGroups.nodes ?? []),
    ...(valueData?.ledger?.ledgerEntryGroups.nodes ?? []),
    ...(keyValueData?.ledger?.ledgerEntryGroups.nodes ?? []),
  ];
  if (groups.length === 0 || !schemaKey) {
    return null;
  }
  return (
    <div className="flex flex-col pt-f2">
      <span className="text-main-500">Group</span>
      {groups.map((group) => (
        <Link
          key={`${group.key}:${group.value}`}
          href={makePath([
            ledgerId,
            'groups',
            `${group.key}:${group.value}`,
            'entries',
          ])}
          onClick={onNavigate}
          className="border-[0.5px] border-b-0 last:border-b-[0.5px] border-main-300 hover:bg-negative"
        >
          <div className="flex flex-row justify-between">
            <div className="flex flex-row flex-nowrap items-center">
              <Icon type="entryGroup" size="sm" />
              <div className="truncate mr-[1ch]">
                {group.key}:{group.value}
              </div>
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
};

const EntityButtons = ({
  activeNoun,
  setActiveNoun,
}: {
  activeNoun?: 'accounts' | 'entries' | 'groups';
  setActiveNoun: (noun: 'accounts' | 'entries' | 'groups' | undefined) => void;
}) => (
  <div className="flex flex-row space-x-f1 pt-f1">
    <button
      type="button"
      data-testid="search-accounts-button"
      className={clsx([
        activeNoun === 'accounts'
          ? 'bg-negative text-main'
          : 'bg-transparent text-main-500 hover:text-main',
        'border-[0.5px] border-main-300',
      ])}
      onClick={() =>
        setActiveNoun(activeNoun === 'accounts' ? undefined : 'accounts')
      }
      tabIndex={-1}
    >
      Accounts
    </button>
    <button
      data-testid="search-entries-button"
      type="button"
      className={clsx([
        activeNoun === 'entries'
          ? 'bg-negative text-main'
          : 'bg-transparent text-main-500 hover:text-main',
        'border-[0.5px] border-main-300',
      ])}
      onClick={() =>
        setActiveNoun(activeNoun === 'entries' ? undefined : 'entries')
      }
      tabIndex={-1}
    >
      Entries
    </button>
    <button
      data-testid="search-groups-button"
      type="button"
      className={clsx([
        activeNoun === 'groups'
          ? 'bg-negative text-main'
          : 'bg-transparent text-main-500 hover:text-main',
        'border-[0.5px] border-main-300',
      ])}
      onClick={() =>
        setActiveNoun(activeNoun === 'groups' ? undefined : 'groups')
      }
      tabIndex={-1}
    >
      Groups
    </button>
  </div>
);

export const SearchModal = ({
  onNavigate,
  onClose,
}: {
  onNavigate: VoidFunction;
  onClose: VoidFunction;
}) => {
  const [searchTerm, setSearchTerm] = useState<string | undefined>();
  const [activeNoun, setActiveNoun] = useState<
    'accounts' | 'entries' | 'groups' | undefined
  >(undefined);
  const { schemaKey, ledgerId } = useLedger();
  const enabled = !!ledgerId;
  return (
    <Modal
      data-testid="search-modal"
      onClose={onClose}
      width="sm"
      body={
        <div className="flex flex-col">
          <div className="relative">
            <TextInput
              data-testid="search-input"
              padding="p-f0"
              autoFocus
              width="w-full"
              align="left"
              size="md"
              value={searchTerm}
              placeholder={enabled ? 'Search' : 'Create a ledger to search'}
              disabled={!enabled}
              onChange={(e) => {
                setSearchTerm(e.target.value);
              }}
            />
            <button
              data-testid="search-clear"
              className="absolute right-f0 top-f0 text-main-500 hover:text-main"
              type="button"
              onClick={() => setSearchTerm('')}
              aria-label="close"
              tabIndex={-1}
            >
              <Icon type="close" size="md" />
            </button>
          </div>
          <Suspense
            fallback={
              <div className="flex flex-row justify-between">
                <EntityButtons
                  activeNoun={activeNoun}
                  setActiveNoun={setActiveNoun}
                />
                <div className="pt-f1">
                  <LoadingIndicator />
                </div>
              </div>
            }
          >
            {enabled && (
              <EntityButtons
                activeNoun={activeNoun}
                setActiveNoun={setActiveNoun}
              />
            )}
            {searchTerm && enabled && (
              <div className="flex flex-col">
                {(activeNoun === 'accounts' || !activeNoun) && (
                  <AccountSearch
                    searchTerm={searchTerm}
                    onNavigate={onNavigate}
                    schemaKey={schemaKey}
                    ledgerId={ledgerId}
                  />
                )}
                {(activeNoun === 'entries' || !activeNoun) && (
                  <EntrySearch
                    searchTerm={searchTerm}
                    onNavigate={onNavigate}
                    schemaKey={schemaKey}
                    ledgerId={ledgerId}
                  />
                )}
                {(activeNoun === 'groups' || !activeNoun) && (
                  <GroupSearch
                    searchTerm={searchTerm}
                    onNavigate={onNavigate}
                    schemaKey={schemaKey}
                    ledgerId={ledgerId}
                  />
                )}
              </div>
            )}
          </Suspense>
        </div>
      }
    />
  );
};
